import { AssistantCitation } from '@services/modules/AssistantStream'
import dayjs from 'dayjs'

import { AssistantDateOptions, renderAssistantDates } from './assistantDates'

/*
 * The sessions a calendar search lists reach the user as a table the server sends (a
 * session_table part: parts/SessionTablePart), each row opening its session by its id. A session
 * the answer names in a sentence has no number to name it by: the answer names it by its start, written as a
 * date token ({{datetime:<start>|<zone>}}). The server cites each session it listed under that
 * start exactly as the tool result gave it; here the citation takes the text that start renders to
 * in this answer, so the time on screen is the link. A session whose start the answer does not
 * write is left out, and two sessions at the same time under the same text are told apart by
 * nothing - neither is linked (citationLinks drops a label two records share).
 */

export const SESSION_CITATION = 'appointment_session'

const DATETIME_TOKEN = /\{\{datetime:([^}|\s\\]+)(?:\\?\|([^}\s]+))?\}\}/g

/** The instant of a start or token value; values without an offset are UTC by contract. */
const instantOf = (value: string): number | null => {
  const instant = dayjs.utc(value)
  return instant.isValid() ? instant.valueOf() : null
}

/**
 * The citations of an answer with each session citation relabelled to the text its start is shown
 * as (once per way the answer writes it); the start itself stays in `start`.
 */
export const labelSessionCitations = (
  content: string,
  citations: AssistantCitation[] | undefined,
  options: AssistantDateOptions
): AssistantCitation[] => {
  const all = citations ?? []
  if (!content || !all.some(c => c.type === SESSION_CITATION)) {
    return all
  }

  const shownAs = new Map<number, { text: string; timeZone?: string }[]>()
  for (const match of content.matchAll(DATETIME_TOKEN)) {
    const instant = instantOf(match[1])
    if (instant === null) {
      continue
    }
    const text = renderAssistantDates(match[0], {
      ...options,
      streaming: false,
    })
    const texts = shownAs.get(instant) ?? []
    if (!texts.some(shown => shown.text === text)) {
      shownAs.set(instant, [...texts, { text, timeZone: match[2] }])
    }
  }

  return all.flatMap(citation => {
    if (citation.type !== SESSION_CITATION) {
      return [citation]
    }
    const instant = instantOf(citation.label)
    // Two cities' sessions can start at the same instant (08:00 in Sydney and in Canberra): a
    // session takes only the texts written in its own zone. A token without a zone, or a citation
    // saved before sessions carried theirs, matches on the instant alone.
    const texts = (instant === null ? [] : shownAs.get(instant) ?? []).filter(
      shown =>
        !citation.timeZone ||
        !shown.timeZone ||
        shown.timeZone === citation.timeZone
    )
    return texts.map(shown => ({
      ...citation,
      label: shown.text,
      start: citation.label,
      timeZone: shown.timeZone ?? citation.timeZone,
    }))
  })
}
